import { Capacitor, registerPlugin } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { HapticPattern, HAPTIC_MAPPING, HAPTIC_STRENGTH } from "./haptics_constants.js";
import { TelegramAPISettings } from "../telegram_api_settings.js";
import { isIOSWebWithoutNativeHaptics } from "../utils/runtime_platform.js";
import { SimulationContext } from "../simulation/simulation_context.js";

export enum VibrationMode
{
  COMBO_ONLY = 0,
  PREVIEW_ONLY = 1,
  FULL = 2
}

export enum VibrationContext
{
  COMBO = "combo",
  PREVIEW = "preview",
  GENERAL = "general"
}

interface StrongHapticsPlugin
{
  vibrate( options: { duration: number; amplitude: number } ): Promise<void>;
}

const StrongHaptics = registerPlugin<StrongHapticsPlugin>( "StrongHaptics" );

export class HapticsController
{
  public is_haptics_enabled: boolean = true;
  public vibration_mode: VibrationMode = VibrationMode.FULL;
  private last_vibration_time: number = 0;
  private readonly MIN_VIBRATION_INTERVAL: number = 50;
  private readonly MAX_VIBRATIONS_PER_SECOND: number = 10;
  private vibration_count: number = 0;
  private vibration_window_start: number = 0;
  private visual_fallback_active: boolean = false;

  constructor()
  {
    this.is_haptics_enabled = StatisticsStorage.load().haptics_enabled ?? true;
  }

  private isIOSWebWithoutNativeHaptics(): boolean
  {
    return isIOSWebWithoutNativeHaptics();
  }

  private isTelegramHapticsAvailable(): boolean
  {
    if ( !TelegramAPISettings.isTelegramEnvironment() )
    {
      return false;
    }

    const webapp: any = ( window as any )?.Telegram?.WebApp;
    if ( !webapp?.HapticFeedback?.impactOccurred )
    {
      return false;
    }

    if ( typeof webapp.isVersionAtLeast === "function" )
    {
      return webapp.isVersionAtLeast( "6.1" );
    }

    const version = parseFloat( String( webapp.version || "0" ) );
    return Number.isFinite( version ) && version >= 6.1;
  }

  private checkHapticSupport(): boolean
  {
    const has_capacitor_haptics = Capacitor.isNativePlatform();
    const has_telegram_haptics: boolean = this.isTelegramHapticsAvailable();
    const has_web_vibrate: boolean = typeof navigator.vibrate === "function";

    return has_capacitor_haptics || has_telegram_haptics || has_web_vibrate;
  }

  private canVibrate(): boolean
  {
    const now: number = Date.now();

    if ( now - this.last_vibration_time < this.MIN_VIBRATION_INTERVAL )
    {
      return false;
    }

    if ( now - this.vibration_window_start >= 1000 )
    {
      this.vibration_count = 0;
      this.vibration_window_start = now;
    }

    if ( this.vibration_count >= this.MAX_VIBRATIONS_PER_SECOND )
    {
      return false;
    }

    return true;
  }

  private getFallbackDuration( pattern: HapticPattern | number[] ): number | number[]
  {
    if ( Array.isArray( pattern ) )
    {
      return pattern;
    }

    return HAPTIC_STRENGTH[pattern].duration;
  }

  private getSingleFallbackDuration( pattern: HapticPattern | number[] ): number
  {
    const duration = this.getFallbackDuration( pattern );
    return Array.isArray( duration )
      ? duration.reduce( ( sum, value ) => sum + value, 0 )
      : duration;
  }

  private getNativeStrength( pattern: HapticPattern | number[] ): { duration: number; amplitude: number }
  {
    if ( !Array.isArray( pattern ) )
      return HAPTIC_STRENGTH[pattern];

    return {
      duration: this.getSingleFallbackDuration( pattern ),
      amplitude: HAPTIC_STRENGTH[HapticPattern.MEDIUM].amplitude,
    };
  }

  private getImpactStyle( pattern: HapticPattern | number[] ): ImpactStyle
  {
    if ( Array.isArray( pattern ) ) return ImpactStyle.Medium;

    switch ( pattern )
    {
      case HapticPattern.LIGHT: return ImpactStyle.Light;
      case HapticPattern.HEAVY: return ImpactStyle.Heavy;
      default: return ImpactStyle.Medium;
    }
  }

  private getVisualFallbackConfig( pattern: HapticPattern | number[] ): { distance: number; duration: number }
  {
    if ( Array.isArray( pattern ) )
    {
      return { distance: 8, duration: 180 };
    }

    switch ( pattern )
    {
      case HapticPattern.LIGHT:
        return { distance: 4, duration: 110 };
      case HapticPattern.MEDIUM:
        return { distance: 7, duration: 140 };
      case HapticPattern.HEAVY:
        return { distance: 10, duration: 180 };
      default:
        return { distance: 5, duration: 120 };
    }
  }

  private triggerVisualFallback( pattern: HapticPattern | number[] ): void
  {
    if ( this.visual_fallback_active )
    {
      return;
    }

    const root = document.body;
    if ( !root || typeof root.animate !== "function" )
    {
      return;
    }

    const { distance, duration } = this.getVisualFallbackConfig( pattern );
    this.visual_fallback_active = true;

    const animation = root.animate(
      [
        { transform: "translate3d(0, 0, 0) scale(1)" },
        { transform: `translate3d(${distance}px, 0, 0) scale(1.002)` },
        { transform: `translate3d(${-distance}px, 0, 0) scale(0.998)` },
        { transform: `translate3d(${Math.round( distance * 0.5 )}px, 0, 0) scale(1.001)` },
        { transform: "translate3d(0, 0, 0) scale(1)" }
      ],
      {
        duration,
        easing: "ease-out"
      }
    );

    animation.onfinish = () =>
    {
      this.visual_fallback_active = false;
      root.style.transform = "";
    };

    animation.oncancel = () =>
    {
      this.visual_fallback_active = false;
      root.style.transform = "";
    };
  }

  public async vibrate( pattern: HapticPattern | number[], context: VibrationContext = VibrationContext.GENERAL ): Promise<void>
  {
    if ( SimulationContext.enabled ) return;
    if ( !this.is_haptics_enabled ) return;
    if ( !this.shouldVibrateForContext( context ) ) return;
    if ( !this.canVibrate() ) return;

    const now: number = Date.now();
    this.last_vibration_time = now;
    this.vibration_count++;

    try
    {
      const platform = Capacitor.getPlatform();
      if ( platform === "android" )
      {
        const strength = this.getNativeStrength( pattern );
        try
        {
          await StrongHaptics.vibrate( strength );
        }
        catch ( error )
        {
          console.warn( "[Haptics] StrongHaptics unavailable, using Capacitor fallback", error );
          await Haptics.vibrate( { duration: strength.duration } );
        }
        return;
      }

      if ( platform === "ios" )
      {
        await Haptics.impact( { style: this.getImpactStyle( pattern ) } );
        return;
      }

      const webapp: any = ( window as any )?.Telegram?.WebApp;
      const telegram = webapp?.HapticFeedback;
      if ( this.isTelegramHapticsAvailable() && telegram?.impactOccurred )
      {
        const telegramStyle = typeof pattern === "string" ? pattern : "medium";
        telegram.impactOccurred( telegramStyle );
        return;
      }

      if ( typeof navigator.vibrate === "function" )
      {
        navigator.vibrate( this.getFallbackDuration( pattern ) );
        return;
      }

      if ( this.isIOSWebWithoutNativeHaptics() )
      {
        this.triggerVisualFallback( pattern );
      }
    }
    catch ( error )
    {
      console.error( "[Haptics] Vibration failed:", error );
    }
  }

  public async vibrateForSound( sound_clip: string ): Promise<void>
  {
    const haptic_pattern = HAPTIC_MAPPING[sound_clip];

    if ( haptic_pattern )
      await this.vibrate( haptic_pattern, VibrationContext.GENERAL );
  }

  private shouldVibrateForContext( context: VibrationContext ): boolean
  {
    switch ( this.vibration_mode )
    {
      case VibrationMode.COMBO_ONLY:
        return context === VibrationContext.COMBO;
      case VibrationMode.PREVIEW_ONLY:
        return context === VibrationContext.PREVIEW;
      case VibrationMode.FULL:
        return true;
      default:
        return false;
    }
  }

  public enableHaptics( status: boolean ): void
  {
    this.is_haptics_enabled = status;
    StatisticsStorage.update( { haptics_enabled: status } );
  }

  public isHapticsEnabled(): boolean
  {
    return this.is_haptics_enabled;
  }

  public isHapticsSupported(): boolean
  {
    return this.checkHapticSupport();
  }

  public setVibrationMode( mode: VibrationMode ): void
  {
    this.vibration_mode = mode;
  }

  public getVibrationMode(): VibrationMode
  {
    return this.vibration_mode;
  }

  public async vibratePreview(): Promise<void>
  {
    await this.vibrate( HapticPattern.LIGHT, VibrationContext.PREVIEW );
  }
}

export const hapticsController = new HapticsController();
